import React, { useState } from 'react'
import { Card } from '@/components/ui'
import { Search, Trophy, TrendingUp } from 'lucide-react'
import { getStudioPosition } from '@/lib/rankings'

interface QuickRankingSearchProps {
  className?: string
}

export function QuickRankingSearch({ className = '' }: QuickRankingSearchProps) {
  const [searchTerm, setSearchTerm] = useState('')
  const [loading, setLoading] = useState(false)
  const [results, setResults] = useState<{
    studioId: number
    global: number
    regional: number
    sizeGroup: number
  } | null>(null)
  const [error, setError] = useState<string | null>(null)

  const handleSearch = async () => {
    const studioId = parseInt(searchTerm)
    
    if (!studioId || studioId <= 0) {
      setError('Введите корректный ID студии')
      return
    }

    try {
      setLoading(true)
      setError(null)
      
      const position = await getStudioPosition(studioId)
      
      if (!position) {
        setError('Студия не найдена или нет данных за текущий месяц')
        setResults(null)
      } else {
        setResults({
          studioId,
          ...position
        })
      }
    } catch (err) {
      setError('Ошибка при поиске данных')
      setResults(null)
    } finally {
      setLoading(false)
    }
  }

  const handleKeyPress = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') {
      handleSearch()
    }
  }

  return (
    <Card className={className}>
      <div className="space-y-4">
        <h3 className="text-lg font-semibold flex items-center gap-2">
          <Search className="w-5 h-5" />
          Быстрый поиск позиции студии
        </h3>
        
        <div className="flex gap-3">
          <input
            type="number"
            placeholder="ID студии..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            onKeyPress={handleKeyPress}
            className="flex-1 px-3 py-2 border border-border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary"
          />
          <button
            onClick={handleSearch}
            disabled={loading}
            className="px-6 py-2 bg-primary text-primary-foreground rounded-lg hover:opacity-90 transition-opacity disabled:opacity-50"
          >
            {loading ? 'Поиск...' : 'Найти'}
          </button>
        </div>

        {error && (
          <div className="p-3 bg-red-50 border border-red-200 rounded-lg">
            <p className="text-red-600 text-sm">{error}</p>
          </div>
        )}

        {results && (
          <div className="space-y-3">
            <div className="flex items-center gap-2 text-success">
              <Trophy className="w-5 h-5" />
              <span className="font-medium">Позиции студии #{results.studioId}</span>
            </div>
            
            <div className="grid grid-cols-3 gap-3">
              <div className="text-center p-3 bg-primary/5 rounded-lg">
                <div className="text-2xl font-bold text-primary">#{results.global}</div>
                <div className="text-xs text-muted-foreground">Среди всех</div>
              </div>
              
              <div className="text-center p-3 bg-info/5 rounded-lg">
                <div className="text-2xl font-bold text-info">#{results.regional}</div>
                <div className="text-xs text-muted-foreground">В регионе</div>
              </div>
              
              <div className="text-center p-3 bg-secondary/5 rounded-lg">
                <div className="text-2xl font-bold text-foreground">#{results.sizeGroup}</div>
                <div className="text-xs text-muted-foreground">В группе</div>
              </div>
            </div>

            <p className="text-xs text-muted-foreground text-center">
              Данные за текущий месяц по выручке
            </p>
          </div>
        )}
      </div>
    </Card>
  )
}